import { useEffect, useState } from "react";
import { toast } from "sonner";
import { MessageSquare, Star } from "lucide-react";
import api, { errMsg } from "@/lib/api";
import DoctorShell from "@/components/DoctorShell";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

/**
 * Phase 4 — Doctor's public feedback view (read-only).
 */
export default function DoctorFeedback() {
    const [data, setData] = useState({ items: [], total: 0, avg_rating: 0 });
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        api.get("/me/feedback")
            .then((r) => setData(r.data))
            .catch((e) => toast.error(errMsg(e, "Failed to load feedback")))
            .finally(() => setLoading(false));
    }, []);

    if (loading) return <DoctorShell><div className="text-[#17212B]/60">Loading…</div></DoctorShell>;

    return (
        <DoctorShell>
            <div className="mb-6" data-testid="doctor-feedback-header">
                <div className="text-xs uppercase tracking-widest text-[#0F766E] font-semibold">Feedback from your patients</div>
                <h1 className="text-2xl font-bold text-[#123B5D] flex items-center gap-2">
                    <MessageSquare size={20} /> Public Feedback
                </h1>
                <p className="text-sm text-[#17212B]/60 mt-1">
                    Submitted via the feedback form on your public profile. Feedback marked hidden by our team is not shown here.
                </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                <Card className="border-[#123B5D]/10">
                    <CardContent className="p-4">
                        <div className="text-xs text-[#17212B]/50">Total</div>
                        <div className="text-2xl font-bold text-[#123B5D]" data-testid="fb-total">{data.total}</div>
                    </CardContent>
                </Card>
                <Card className="border-[#123B5D]/10">
                    <CardContent className="p-4">
                        <div className="text-xs text-[#17212B]/50">Average rating</div>
                        <div className="text-2xl font-bold text-[#0F766E] flex items-center gap-2" data-testid="fb-avg">
                            <Star size={18} className="fill-[#D97706] text-[#D97706]" />
                            {data.avg_rating || 0}
                        </div>
                    </CardContent>
                </Card>
            </div>

            <Card className="border-[#123B5D]/10">
                <CardHeader><CardTitle className="text-base">Recent feedback</CardTitle></CardHeader>
                <CardContent>
                    {data.items.length ? (
                        <ul className="divide-y">
                            {data.items.map((f) => (
                                <li key={f.id} className="py-3" data-testid={`fb-item-${f.id}`}>
                                    <div className="flex justify-between items-center">
                                        <div className="font-semibold text-[#123B5D]">{f.name}</div>
                                        <div className="flex items-center gap-1 text-[#D97706]">
                                            {[1,2,3,4,5].map((n) => (
                                                <Star key={n} size={14} className={n <= f.rating ? "fill-[#D97706] text-[#D97706]" : "text-[#D97706]/25"} />
                                            ))}
                                        </div>
                                    </div>
                                    <p className="text-sm text-[#17212B]/80 mt-1">{f.message}</p>
                                    <div className="text-xs text-[#17212B]/50 mt-1">{(f.created_at || "").split("T")[0]}</div>
                                </li>
                            ))}
                        </ul>
                    ) : (
                        <p className="text-sm text-[#17212B]/60">No public feedback yet. Share your public profile link to invite reviews.</p>
                    )}
                </CardContent>
            </Card>
        </DoctorShell>
    );
}
